/**
 * Plan Package
 *
 * Signal-driven, plan-based homepage system.
 * Exports types, journey definitions, hooks, and live homepage components.
 */

// Types
export * from "./plan.types";
export { getJourneyStepCompletionId } from "./completion";
export { resolvePlannedModality } from "./resolvePlannedModality";
export { resolveAcceptedPlanModality } from "./resolveAcceptedPlanModality";
export {
  assertValidJourneyActions,
  validateJourneyActions,
} from "./actionValidation";

// Journeys
export {
  JOURNEY_REGISTRY,
  resolveJourneyKey,
  getJourneyForState,
  PREGNANCY_EXPLORING,
  EGG_FREEZING_SOLO,
  EMBRYO_FREEZING_PARTNER,
  IVF_SOLO,
  IVF_PARTNER,
  IVF_DONOR,
  IVF_SURROGATE,
  IVF_SOLO_MALE,
  IVF_PARTNER_MALE,
  IVF_DONOR_MALE,
  IVF_SURROGATE_MALE,
  IUI_PARTNER,
  IUI_PARTNER_MALE,
  SPERM_FREEZING_SOLO,
  EGG_DONATION,
  SPERM_DONATION,
} from "./journeys";

// Hooks
export { useHomepageCards } from "./hooks/useHomepageCards";
export { usePlanProgress } from "./hooks/usePlanProgress";
export { useTreatmentDashboard } from "./hooks/useTreatmentDashboard";
export { useCycleComplete } from "./hooks/useCycleComplete";

// Components
export { default as OptimizationCard } from "./components/OptimizationCard";
export { default as OptimizationCardV2 } from "./components/OptimizationCardV2";
export { default as PartnerHomeView } from "./components/PartnerHomeView";
export { PartnerHomeUnavailableView } from "./components/PartnerHomeView";
export { FETParticipantHomeView } from "./components/FETParticipantHomeView";
export { default as CycleCompleteView } from "./components/CycleCompleteView";
export { default as ConditionInsightCard } from "./components/ConditionInsightCard";
export { default as TestResultInsightCard } from "./components/TestResultInsightCard";
export { default as CostEstimateCard } from "./components/CostEstimateCard";
export {
  PreCycleHome,
  type PreCycleHomeSection,
} from "./components/PreCycleHome";
export {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./components/TimelineTaskComponents";
export { Stepper, StepperSection } from "./components/Stepper";
export {
  ClinicShortlistSection,
  type ClinicShortlistCriterion,
  type ClinicShortlistMatch,
} from "./components/ClinicShortlistSection";
export { JourneyHandoffCard } from "./components/JourneyHandoffCard";

// Intelligence layer
export {
  useConditionInsights,
  useTestResultInsights,
  resultInterpretationKey,
  useCostEstimate,
  getConditionContentForStep,
  CONDITION_CONTENT_REGISTRY,
} from "./intelligence";
export type {
  ConditionStepContent,
  ConditionInsight,
  ResultInterpretation,
  CostEstimate,
  CostLineItem,
} from "./intelligence";

// Experience layer
export {
  getStepEmotionalContext,
  getTreatmentEmotionalContext,
  getContextualSubline,
} from "./experience";
export type { EnhancedEmotionalContext } from "./experience";
